import StatusChip from 'components/@extended/StatusChip';
import { OFF_ACTION_CLASS, OFF_OUTLINED_CLASS, OFF_SECONDARY_CLASS } from 'components/buttons/buttonStyle';
import PinButton from 'components/buttons/PinButton';
import ExternalIntegrationLink from 'components/ExternalIntegrationLink';
import { CircleAlert, ClipboardCheckIcon, HistoryIcon } from 'components/icons';
import { InvoiceCommentsButton } from 'components/invoice-comments/InvoiceCommentsButton';
import { FormattedDate } from 'components/misc/FormattedDate';
import { MenuItem } from 'components/navigation/KebabMenu';
import PageHeader from 'components/PageHeader';
import PartyName from 'components/party/PartyName';
import { RecordChip } from 'components/record-chip/RecordChip';
import {
    META_ROW_CLASS,
    MetaItem,
    OUTLINED_ACTION_CLASS,
    PRIMARY_ACTION_CLASS,
    RecordMoreMenu,
} from 'components/record-header/RecordHeader';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import Unmasked from 'components/Unmasked';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { INVOICE_LEARNING_KEYS } from 'pages/invoices/invoiceLearningHints';
import { InvoiceWorkflowAction } from 'pages/invoices/InvoiceWorkflow';
import { formatShareRate } from 'pages/invoices/payers/paymentShare';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { getInvoiceStatusChipType, getInvoiceTotalsAmounts } from 'pages/invoices/util/invoiceUtil';
import { Ref, useId } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { useIsNetvisorIntegrationEnabledQuery } from 'store/api/netvisor-integration';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, InvoiceStatusType, InvoiceType } from 'types/invoices';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { recordPin } from 'utils/pins';

const READ_AUDIT_TRAIL = ['READ.INVOICES.AUDIT_TRAIL'];

/** The type chip names the type in full; a type it does not know reads as its classifier does. */
const TYPE_LABEL_ID: Record<string, string> = {
    [InvoiceType.DEBIT]: 'invoices.general-tab.details.debit-invoice',
    [InvoiceType.CREDIT]: 'invoices.general-tab.details.credit-invoice',
    [InvoiceType.CORRECTION]: 'invoices.general-tab.details.correction-invoice',
    [InvoiceType.PREPAYMENT]: 'invoices.general-tab.details.prepayment-invoice',
};

// On a phone the kit's transparent 1px border goes: without those 4px Postpone, Reject and Approve share a row.
const ACTION_LOOK_CLASS: Record<InvoiceWorkflowAction['look'], string> = {
    primary: cn(PRIMARY_ACTION_CLASS, OFF_SECONDARY_CLASS, 'max-sm:border-0'),
    outlined: cn(OUTLINED_ACTION_CLASS, OFF_OUTLINED_CLASS, 'border px-3.5 font-medium'),
    plain: cn(
        'text-foreground h-9 rounded-lg px-3.5 font-medium max-sm:border-0',
        OFF_ACTION_CLASS,
        'aria-disabled:text-foreground/50 aria-disabled:hover:text-foreground/50 aria-disabled:hover:bg-transparent'
    ),
};

const ACTION_VARIANT = { primary: 'secondary', outlined: 'outline', plain: 'ghost' } as const;

/** On a phone the payer and the case each take a row of the meta row; from `sm` they are its items as they are. */
const PHONE_ROW_CLASS = 'contents max-sm:flex max-sm:basis-full';

/** A mark beside the status whose meaning lives in its tooltip: a Tab stop, the words its description too. */
function MarkWithTip({ label, tip }: { label: string; tip: string }) {
    const tipId = useId();
    return (
        <>
            <Tooltip content={tip}>
                <span tabIndex={0} aria-describedby={tipId} className={cn('inline-flex rounded-lg', FOCUS_RING)}>
                    <StatusChip type="neutral" label={label} />
                </span>
            </Tooltip>
            <span id={tipId} hidden>
                {tip}
            </span>
        </>
    );
}

interface Props {
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    workflowActions: InvoiceWorkflowAction[];
    menuItems: MenuItem[];
    onOpenHistory: () => void;
    /** The Comments button's state, when the user may read the comments; `isDrawerOpen` while the drawer hosts them. */
    comments?: { isOpen: boolean; isDrawerOpen: boolean; onOpen: () => void; buttonRef: Ref<HTMLButtonElement> };
}

/**
 * The invoice's header in the new design, built from the record header's pieces as the case's is: the type, status
 * and marks over the number, a meta row of the payer, the cases, the dates and the amount, then the actions: History,
 * Comments, the workflow's buttons for the status (the primary last), ⋯. A workflow button that waits keeps its
 * focus and says why in a line under the actions, which it is described by.
 */
export function InvoiceRecordHeader({
    invoice,
    classifiers,
    workflowActions,
    menuItems,
    onOpenHistory,
    comments,
}: Props) {
    const reasonIdBase = useId();
    const canReadAuditTrail = useHasPermissions(READ_AUDIT_TRAIL);
    const { data: isNetvisorEnabled } = useIsNetvisorIntegrationEnabledQuery();
    const { isSharedPaymentCopy } = useSharedPayment(invoice);
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);

    const status = invoice.invoiceStatusTypeCode;
    // Once paid or credited nothing is payable: the band names the invoice's total instead.
    const isSettled = status === InvoiceStatusType.PAID || status === InvoiceStatusType.CREDITED;
    const { totalWithVatPaymentCurrency } = getInvoiceTotalsAmounts(invoiceTotals);
    const prepayment = invoiceTotals?.paymentCurrency.invoice.prepaymentAmount ?? 0;
    const isJointInvoice = (invoice.invoiceMatters?.length ?? 0) > 1;

    const reasons = [
        ...new Set(workflowActions.flatMap((action) => (action.disabled && action.reason ? [action.reason] : []))),
    ];
    const reasonId = (reason: string) => `${reasonIdBase}-${reasons.indexOf(reason)}`;

    return (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
            <div className="flex min-w-0 flex-auto flex-col gap-2 lg:min-w-[16rem] lg:flex-[1_1_0%]">
                <div className="flex flex-wrap items-center gap-1.5">
                    <StatusChip
                        type="neutral"
                        label={
                            TYPE_LABEL_ID[invoice.invoiceTypeCode]
                                ? t(TYPE_LABEL_ID[invoice.invoiceTypeCode])
                                : getClassifierText(classifiers['INVOICE_TYPE'], invoice.invoiceTypeCode)
                        }
                    />
                    {invoice.isSplit ? <StatusChip type="neutral" label={t('invoices.split-invoice')} /> : null}
                    {isSharedPaymentCopy ? (
                        <MarkWithTip
                            label={t('invoices.payers.shared-payment-chip', {
                                // A whole share reads without its decimals: "50%".
                                rate: String(Number(formatShareRate(invoice.paymentShareRate))),
                            })}
                            tip={t('invoices.payers.shared-payment-chip-tooltip', {
                                position: String(invoice.sharedPaymentGroup?.position ?? 1),
                                count: String(invoice.sharedPaymentGroup?.invoiceCount ?? 1),
                            })}
                        />
                    ) : null}
                    {isJointInvoice ? <StatusChip type="neutral" label={t('invoices.joint-invoice')} /> : null}
                    <StatusChip
                        type={getInvoiceStatusChipType(status)}
                        label={getClassifierText(classifiers['INVOICE_STATUS_TYPE'], status)}
                    />
                    <PinButton
                        pin={recordPin(
                            'invoice',
                            invoice.id,
                            // A draft has no number yet.
                            invoice.invoiceNumber || `${t('invoices.invoice')} · ${invoice.clientParty.name}`
                        )}
                    />
                    {isNetvisorEnabled && invoice.accountingIntegrationInvoiceUrl ? (
                        <ExternalIntegrationLink href={invoice.accountingIntegrationInvoiceUrl} label="Netvisor" />
                    ) : null}
                </div>
                <PageHeader
                    showBreadcrumb
                    title={invoice.invoiceNumber || ''}
                    titleNode={
                        <span className="tabular-nums">
                            <Unmasked>{invoice.invoiceNumber || ''}</Unmasked>
                        </span>
                    }
                >
                    <div className={META_ROW_CLASS}>
                        {invoice.clientParty?.id ? (
                            <span className={PHONE_ROW_CLASS}>
                                <MetaItem label={<FormattedMessage id="payer" />}>
                                    <PartyName party={invoice.clientParty} isLink showPartyNumber />
                                </MetaItem>
                            </span>
                        ) : null}
                        {invoice.invoiceMatters?.length ? (
                            <span className={PHONE_ROW_CLASS}>
                                <MetaItem
                                    label={
                                        <FormattedMessage id={isJointInvoice ? 'matters.matters' : 'matters.case'} />
                                    }
                                >
                                    {isJointInvoice ? (
                                        // A joint invoice names each case by its number, as the invoices list does.
                                        invoice.invoiceMatters.map((invoiceMatter) => (
                                            <RecordChip
                                                key={invoiceMatter.matterId}
                                                entityType="MATTER"
                                                id={invoiceMatter.matterId}
                                                label={invoiceMatter.matterNumber}
                                                hitArea={false}
                                            />
                                        ))
                                    ) : (
                                        <RecordChip
                                            entityType="MATTER"
                                            id={invoice.invoiceMatters[0].matterId}
                                            label={invoice.invoiceMatters[0].fullName}
                                            hitArea
                                        />
                                    )}
                                </MetaItem>
                            </span>
                        ) : null}
                        {invoice.invoiceDate ? (
                            <MetaItem label={<FormattedMessage id="invoices.invoice-date" />}>
                                <span className="tabular-nums">
                                    <FormattedDate value={invoice.invoiceDate} />
                                </span>
                            </MetaItem>
                        ) : null}
                        {invoice.dueDate ? (
                            <MetaItem label={<FormattedMessage id="invoices.due-date" />}>
                                <span className="tabular-nums">
                                    <FormattedDate value={invoice.dueDate} />
                                </span>
                            </MetaItem>
                        ) : null}
                        {invoiceTotals ? (
                            <MetaItem
                                label={
                                    <FormattedMessage
                                        id={
                                            isSettled
                                                ? 'invoices.totals-tab.table-headings.total-with-vat'
                                                : 'invoices.amount-payable'
                                        }
                                    />
                                }
                            >
                                <span className="whitespace-nowrap tabular-nums">
                                    <MoneyDisplay
                                        amount={
                                            isSettled
                                                ? totalWithVatPaymentCurrency
                                                : totalWithVatPaymentCurrency - prepayment
                                        }
                                        currency={invoiceTotals.paymentCurrency.currencyTypeCode}
                                    />
                                </span>
                            </MetaItem>
                        ) : null}
                    </div>
                </PageHeader>
            </div>
            <div className="flex min-w-0 flex-col gap-2 max-lg:w-full lg:items-end">
                {/* The comments drawer lies over the actions while it is open; the docked column leaves them be. */}
                <div
                    className="flex flex-wrap items-center gap-2 lg:justify-end"
                    inert={comments?.isDrawerOpen}
                    data-learning={INVOICE_LEARNING_KEYS.actions}
                >
                    {canReadAuditTrail ? (
                        <Button
                            variant="outline"
                            className={cn(OUTLINED_ACTION_CLASS, 'size-9 border px-0')}
                            onClick={onOpenHistory}
                            aria-label={t('audit-trail.invoice.button')}
                        >
                            <HistoryIcon className="size-4" />
                        </Button>
                    ) : null}
                    {comments ? (
                        <InvoiceCommentsButton
                            invoiceId={invoice.id}
                            expanded={comments.isOpen}
                            onOpen={comments.onOpen}
                            buttonRef={comments.buttonRef}
                            icon={ClipboardCheckIcon}
                            className={cn(
                                OUTLINED_ACTION_CLASS,
                                'gap-1.5 border px-3 [&>svg]:size-4! max-sm:[&>svg+span]:sr-only'
                            )}
                        />
                    ) : null}
                    {workflowActions.length > 0 ? (
                        <span aria-hidden className="bg-divider mx-1 h-6 w-px max-sm:hidden" />
                    ) : null}
                    {workflowActions.map((action) => (
                        <Button
                            key={action.key}
                            variant={ACTION_VARIANT[action.look]}
                            className={cn(ACTION_LOOK_CLASS[action.look], 'max-sm:flex-1 max-sm:justify-center')}
                            disabled={action.disabled}
                            focusableWhenDisabled
                            aria-describedby={action.disabled && action.reason ? reasonId(action.reason) : undefined}
                            onClick={action.onClick}
                        >
                            <FormattedMessage id={action.labelId} />
                        </Button>
                    ))}
                    <RecordMoreMenu menuItems={menuItems} />
                </div>
                {reasons.map((reason) => (
                    <p
                        key={reason}
                        id={reasonId(reason)}
                        className="text-meta text-warning-ink m-0 flex items-center gap-1.5"
                    >
                        <CircleAlert aria-hidden className="size-4 shrink-0" />
                        {reason}
                    </p>
                ))}
            </div>
        </div>
    );
}
